/**
 * Article-specific access decisions over the content-agnostic metered-unlock
 * primitives in ../metering (period keys, meter state, applyUnlock). What
 * lives HERE is only what is genuinely about articles: the access precedence
 * chain (read articles stay open forever; the anonymous one-free-article
 * device rule) and the legacy free-set migration.
 *
 * Pure and platform-agnostic: wall-clock time enters ONLY through the
 * injected `periodKey` input — same inputs, same decision, golden-testable.
 */

import { METER_DEFAULTS, LEGACY_PERIOD_KEY, getMeterState, applyUnlock, meterEntryFor } from '../metering';

/** Anonymous device-local free-read pool size — total per device, never resets. */
export const ANONYMOUS_FREE_READ_LIMIT = 5;

/**
 * THE access decision — single source for every article surface.
 *
 * Precedence (first match wins):
 *   premium > already_read > already_unlocked
 *   > anonymous branch (no account, device-local free-read pool):
 *       anonymous_free_open      — an article this device already consumed → open
 *       anonymous_free_available — pool not exhausted → lockable (tap consumes)
 *       anonymous_free_consumed  — device spent its whole pool → locked (signup CTA)
 *   > meter_available (lockable) > meter_exhausted (locked)
 *
 * @param {Object} inputs
 * @param {string|number} inputs.articleId
 * @param {boolean} inputs.isSubscribe        derive via isPremiumSubscription (status-aware)
 * @param {boolean} inputs.isAuthenticated    false = anonymous / pre-signup
 * @param {Object}  inputs.readMap            { [articleId]: truthy } — read articles, any era
 * @param {Object}  inputs.unlockMap          { [articleId]: { period_key, ... } }
 * @param {Array<string|number>} [inputs.consumedArticleIds] device-local anonymous free reads
 * @param {number}  [inputs.anonymousLimit]   anonymous pool size (total per device)
 * @param {string}  inputs.periodKey          from getUnlockPeriodKey (injected)
 * @param {number}  [inputs.limit]            per-period unlock count (brand config)
 * @returns {{ access: 'unlocked'|'lockable'|'locked',
 *             reason: 'premium'|'already_read'|'already_unlocked'
 *                    |'anonymous_free_open'|'anonymous_free_available'|'anonymous_free_consumed'
 *                    |'meter_available'|'meter_exhausted',
 *             remaining: number }}
 */
export function deriveArticleAccess({
  articleId,
  isSubscribe,
  isAuthenticated,
  readMap,
  unlockMap,
  consumedArticleIds = [],
  anonymousLimit = ANONYMOUS_FREE_READ_LIMIT,
  periodKey,
  limit = METER_DEFAULTS.UNLOCK_LIMIT,
}) {
  const meter = getMeterState(unlockMap, periodKey, limit);

  if (isSubscribe) {
    return { access: 'unlocked', reason: 'premium', remaining: meter.remaining };
  }
  if (meterEntryFor(readMap, articleId)) {
    return { access: 'unlocked', reason: 'already_read', remaining: meter.remaining };
  }
  if (meterEntryFor(unlockMap, articleId)) {
    return { access: 'unlocked', reason: 'already_unlocked', remaining: meter.remaining };
  }
  if (!isAuthenticated) {
    const consumed = (consumedArticleIds ?? []).filter((id) => id != null).map(String);
    const anonymousRemaining = Math.max(0, anonymousLimit - consumed.length);
    if (consumed.includes(String(articleId))) {
      return { access: 'unlocked', reason: 'anonymous_free_open', remaining: anonymousRemaining };
    }
    if (anonymousRemaining > 0) {
      return { access: 'lockable', reason: 'anonymous_free_available', remaining: anonymousRemaining };
    }
    return { access: 'locked', reason: 'anonymous_free_consumed', remaining: 0 };
  }
  if (meter.remaining > 0) {
    return { access: 'lockable', reason: 'meter_available', remaining: meter.remaining };
  }
  return { access: 'locked', reason: 'meter_exhausted', remaining: 0 };
}

/**
 * Normalize the persisted anonymous free-read stash to the array shape,
 * whatever era wrote it. Accepts the legacy single-stash object
 * (`{ id, topic_id }` — the one-free-read model), the current array of
 * stashes, or junk (null/undefined/strings/entries without an id → dropped).
 * Dedupes by id (first win) and caps at `limit` so storage tampering can
 * never mint extra reads.
 *
 * @param {Object|Array|null|undefined} raw   parsed storage value, any era
 * @param {number} [limit]
 * @returns {Array<{ id: string|number, topic_id?: * }>}
 */
export function normalizeAnonymousStash(raw, limit = ANONYMOUS_FREE_READ_LIMIT) {
  const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const out = [];
  const seen = new Set();
  for (const entry of list) {
    if (out.length >= limit) break;
    const id = entry && typeof entry === 'object' ? entry.id : null;
    if (id == null || seen.has(String(id))) continue;
    seen.add(String(id));
    out.push(entry);
  }
  return out;
}

/**
 * One-time migration grant for existing free users crossing to metering:
 * a legacy-free-set article the user had STARTED (appears as a progress
 * pointer) but not yet marked read gets a permanent `legacy` unlock so the
 * switch never takes away something they were mid-way through. Already-read
 * articles need no grant (readMap already opens them, forever).
 *
 * Additions only; second run is a no-op (`changed: false`).
 *
 * @param {Object} params
 * @param {Object} params.unlockMap
 * @param {Array<string|number>} params.legacyFreeArticleIds
 *        filterToFreeSet(articles, domainList, 'num_articles') ids
 * @param {Object} params.readMap
 * @param {Array}  params.articlesProgress profile.articles_progress
 *        [{ articles_set_id, last_seen_article_id, last_index? }]
 * @param {string} params.nowIso
 * @returns {{ unlockMap: Object, changed: boolean }}
 */
export function buildLegacyMigrationUnlocks({ unlockMap, legacyFreeArticleIds, readMap, articlesProgress, nowIso }) {
  const startedIds = new Set(
    (articlesProgress ?? [])
      .map((p) => p?.last_seen_article_id)
      .filter((id) => id != null)
      .map(String)
  );

  let map = unlockMap ?? {};
  let changed = false;
  for (const id of legacyFreeArticleIds ?? []) {
    if (id == null) continue;
    if (!startedIds.has(String(id))) continue;
    if (meterEntryFor(readMap, id)) continue;
    const next = applyUnlock(map, id, LEGACY_PERIOD_KEY, nowIso, 'legacy_migration');
    if (next.changed) {
      map = next.unlockMap;
      changed = true;
    }
  }
  return { unlockMap: map, changed };
}
